import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  TextField,
  List,
  Typography,
  Box,
  Divider,
  InputAdornment,
  DialogContent
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import CommandBarItem from './CommandBarItem';
import { useCommandBar } from '../../hooks/useCommandBar';

const CommandBar = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef();
  
  const {
    sectionName,
    recentProjects,
    recentItems,
    quickActions,
    filterProjects,
    filterActions,
    handleProjectSwitch,
    handleSearch,
    handleQuickAction,
    trackEvent
  } = useCommandBar();

  // Calculate sections and flat item list for keyboard navigation
  const sections = [
    {
      title: 'Recent Projects',
      items: filterProjects(recentProjects, query),
      type: 'project'
    },
    {
      title: `Recent in ${sectionName}`,
      items: recentItems.slice(0, 5),
      type: 'item'
    },
    {
      title: 'Quick Actions',
      items: filterActions(quickActions, query),
      type: 'action'
    }
  ].filter(section => section.items.length > 0);

  // Flatten all items for keyboard navigation
  const allItems = sections.reduce((acc, section, sectionIndex) => {
    section.items.forEach((item, itemIndex) => {
      acc.push({
        ...item,
        sectionIndex,
        itemIndex,
        sectionType: section.type
      });
    });
    return acc;
  }, []);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      trackEvent('cmdbar.open');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open, trackEvent]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
          
        case 'Enter':
          e.preventDefault();
          if (query.trim() && allItems.length === 0) {
            // No items found, forward search to page
            handleSearch(query);
            handleClose('action');
          } else if (allItems[selectedIndex]) {
            // Select current item
            handleItemClick(allItems[selectedIndex], allItems[selectedIndex].sectionType);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          handleClose('esc');
          break;
          
        default:
          // Let other keys bubble up to input
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedIndex, allItems, query, handleSearch]);

  const handleClose = useCallback((method = 'click') => {
    trackEvent('cmdbar.close', { method });
    setQuery('');
    setSelectedIndex(0);
    onClose();
  }, [onClose, trackEvent]);

  const handleItemClick = useCallback((item, type) => {
    switch (type) {
      case 'project':
        handleProjectSwitch(item);
        break;
      case 'action':
        handleQuickAction(item);
        break;
      case 'item':
        // Handle recent item selection - could navigate or open
        window.dispatchEvent(new CustomEvent('cmdbar.item_select', {
          detail: { item }
        }));
        break;
      default:
        break;
    }
    handleClose('action');
  }, [handleProjectSwitch, handleQuickAction, handleClose]);

  const handleQueryChange = useCallback((e) => {
    setQuery(e.target.value);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (query.trim()) {
      handleSearch(query);
      handleClose('action');
    }
  }, [query, handleSearch, handleClose]);

  // Calculate which item should be highlighted
  const getItemSelected = useCallback((sectionIndex, itemIndex) => {
    let flatIndex = 0;
    for (let i = 0; i < sectionIndex; i++) {
      flatIndex += sections[i].items.length;
    }
    flatIndex += itemIndex;
    return flatIndex === selectedIndex;
  }, [sections, selectedIndex]);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose('click')}
      maxWidth="sm"
      fullWidth
      keepMounted={true} // As per GPT5 guardrails
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          backdropFilter: 'blur(6px)', // ≤6px as per guardrails
          background: 'rgba(255,255,255,0.95)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxHeight: '70vh',
          mt: '10vh'
        }
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': 'Command palette'
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Search Input */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            ref={inputRef}
            value={query}
            onChange={handleQueryChange}
            placeholder="Type to search or select action..."
            fullWidth
            variant="outlined"
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'aria-label': 'Command search input',
              style: { outline: 'none' }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none',
                },
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim() && allItems.length === 0) {
                e.preventDefault();
                handleSearchSubmit();
              }
            }}
          />
        </Box>

        {/* Results Sections */}
        <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
          {sections.length === 0 && query.trim() ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                No results found. Press Enter to search in {sectionName}.
              </Typography>
            </Box>
          ) : sections.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                Start typing to search or use quick actions
              </Typography>
            </Box>
          ) : (
            sections.map((section, sectionIndex) => (
              <Box key={section.title}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    pt: sectionIndex === 0 ? 1 : 2,
                    pb: 0.5,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    display: 'block'
                  }}
                >
                  {section.title}
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {section.items.map((item, itemIndex) => (
                    <CommandBarItem
                      key={`${sectionIndex}-${itemIndex}`}
                      item={item}
                      selected={getItemSelected(sectionIndex, itemIndex)}
                      onClick={(clickedItem) => handleItemClick(clickedItem, section.type)}
                    />
                  ))}
                </List>
                {sectionIndex < sections.length - 1 && (
                  <Divider sx={{ my: 0.5 }} />
                )}
              </Box>
            ))
          )}
        </Box>

        {/* Footer hint */}
        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary">
            ↑↓ navigate • Enter select • Esc close
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CommandBar;